import { Link } from 'react-router'
import type { ReviewStats } from '@/api/client'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { StarBar, StarLegend } from '@/components/reviews/star-distribution'

const SUBRATING_LABEL: Record<string, string> = {
  accuracy: 'accuracy',
  value: 'value',
  cleanliness: 'cleanliness',
  location: 'location',
  check_in: 'check-in',
  communication: 'communication',
}

function ratingCls(v: number | null) {
  if (v == null) return 'text-textMuted'
  if (v >= 4.9) return 'text-mint'
  if (v >= 4.7) return 'text-amber'
  return 'text-danger'
}

function Delta({
  now,
  then,
  digits = 2,
}: {
  now: number | null
  then: number | null
  digits?: number
}) {
  if (now == null || then == null) return <span className="text-textMuted">-</span>
  const d = now - then
  const cls = d > 0 ? 'text-mint' : d < 0 ? 'text-danger' : 'text-textMuted'
  return (
    <span className={`tabular-nums ${cls}`}>
      {d > 0 ? '+' : ''}
      {d.toFixed(digits)}
    </span>
  )
}

/** Worst first: the properties losing stars, with the star mix and where they lose them. */
export function PropertiesTable({ stats }: { stats: ReviewStats }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Properties</CardTitle>
        <CardDescription>
          Worst first. Average rating with this year against last year to date, review rate over
          closed stays, the star mix with threes and below in red, and the weakest sub-rating.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[10px] uppercase tracking-wide text-textMuted">
                <th className="px-2 py-1 text-left font-medium">Property</th>
                <th className="px-2 py-1 text-right font-medium">Avg</th>
                <th className="px-2 py-1 text-right font-medium">This year</th>
                <th className="px-2 py-1 text-right font-medium">vs last</th>
                <th className="px-2 py-1 text-right font-medium">Reviews</th>
                <th className="px-2 py-1 text-right font-medium">Review rate</th>
                <th className="px-2 py-1 text-left font-medium" style={{ minWidth: 180 }}>
                  Star mix
                </th>
                <th className="px-2 py-1 text-right font-medium">3★ and below</th>
                <th className="px-2 py-1 text-left font-medium">Weakest</th>
              </tr>
            </thead>
            <tbody>
              {stats.properties.map(p => (
                <tr key={p.property_id} className="border-t border-border">
                  <td className="whitespace-nowrap px-2 py-1.5 font-medium text-text">
                    <Link to={`/properties/${p.property_id}`} className="hover:underline">
                      {p.name}
                    </Link>
                  </td>
                  <td
                    className={`px-2 py-1.5 text-right font-semibold tabular-nums ${ratingCls(p.avg)}`}
                  >
                    {p.avg == null ? '-' : p.avg.toFixed(2)}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-text">
                    {p.this_year.avg == null ? '-' : p.this_year.avg.toFixed(2)}
                    <span className="text-textMuted"> ({p.this_year.reviews})</span>
                  </td>
                  <td className="px-2 py-1.5 text-right">
                    <Delta now={p.this_year.avg} then={p.last_year.avg} />
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-text">{p.reviews}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span
                          className={
                            p.review_rate != null && p.review_rate < 70
                              ? 'font-semibold text-danger'
                              : 'text-text'
                          }
                        >
                          {p.review_rate == null ? '-' : `${p.review_rate}%`}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        {p.stays - p.no_review} of {p.stays} closed stays reviewed
                        {p.pending_no_review
                          ? ` · ${p.pending_no_review} still inside the window`
                          : ''}
                      </TooltipContent>
                    </Tooltip>
                  </td>
                  <td className="px-2 py-1.5">
                    <StarBar
                      stars={p.stars}
                      noReview={p.no_review}
                      pending={p.pending_no_review}
                      className="h-3.5"
                      ariaLabel={`${p.name}: ${p.stars[5]} five, ${p.stars[4]} four, ${p.three_and_below} three or below, ${p.no_review} no review`}
                    />
                  </td>
                  <td
                    className={`px-2 py-1.5 text-right tabular-nums ${p.three_and_below > 0 ? 'font-semibold text-danger' : 'text-textMuted'}`}
                  >
                    {p.three_and_below}
                  </td>
                  <td className="px-2 py-1.5 text-textMuted">
                    {p.weakest_subrating ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="cursor-default">
                            {SUBRATING_LABEL[p.weakest_subrating] ?? p.weakest_subrating}{' '}
                            {p.subratings[
                              p.weakest_subrating as keyof typeof p.subratings
                            ]?.toFixed(2)}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          {Object.entries(p.subratings)
                            .map(([k, v]) => `${SUBRATING_LABEL[k] ?? k} ${v.toFixed(2)}`)
                            .join(' · ')}
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      '-'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <StarLegend />
      </CardContent>
    </Card>
  )
}
